import { LightButton } from "@lyteflo/components";
import { LIVE_SCAN_ALERT_TYPE, PortalRoutes, ToastLevel } from "@lyteflo/types";
import {
  type LiveScanAlertSpec,
  removeLeadingZeros,
  useLiveScanAlerts,
  useToast,
} from "@lyteflo/utils";
import type { TFunction } from "i18next";
import { useCallback } from "preact/compat";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";

const DEVICE_TOAST_BUTTON_CLASS = "lf-h-6 lf-flex-shrink-0";
const DEVICE_TOAST_CONTENT_CLASS =
  "lf-flex lf-items-center lf-gap-3 lf-flex-wrap";
const DEVICE_TOAST_TEXT_CLASS = "lf-flex-1 lf-min-w-0";

type DeviceToastAction = {
  labelKey: string;
  onClick: () => void;
};

const createDeviceToast = (
  t: TFunction,
  level: ToastLevel,
  textKey: string,
  textParams: { deviceName: string; scanNumber?: string },
  action: DeviceToastAction,
  showActionButton: boolean,
) => ({
  level,
  text: t(textKey, textParams),
  children: (
    <div className={DEVICE_TOAST_CONTENT_CLASS}>
      <span className={DEVICE_TOAST_TEXT_CLASS}>{t(textKey, textParams)}</span>
      {showActionButton && (
        <LightButton
          onClick={action.onClick}
          className={DEVICE_TOAST_BUTTON_CLASS}
        >
          <span className="lf-text-sm">{t(action.labelKey)}</span>
        </LightButton>
      )}
    </div>
  ),
});

export const useLiveScanToasts = () => {
  const { t } = useTranslation();
  const [location, setLocation] = useLocation();
  const { addToast } = useToast();

  const isOnLiveScanRoute = location === PortalRoutes.LIVE_SCANS;

  const viewDetailsAction: DeviceToastAction = {
    labelKey: "liveScan.viewDetails",
    onClick: () => setLocation(PortalRoutes.LIVE_SCANS),
  };

  const onAlert = useCallback(
    (spec: LiveScanAlertSpec) => {
      const params = { deviceName: spec.deviceName };

      if (spec.type === LIVE_SCAN_ALERT_TYPE.NEW_SESSION) {
        addToast(
          createDeviceToast(
            t,
            ToastLevel.INFO,
            "liveScan.toast.newSession",
            params,
            viewDetailsAction,
            !isOnLiveScanRoute,
          ),
        );
        return;
      }

      if (spec.type === LIVE_SCAN_ALERT_TYPE.VIN_NOT_ACQUIRED) {
        addToast(
          createDeviceToast(
            t,
            ToastLevel.INFO,
            "liveScan.toast.vinNotAcquired",
            {
              deviceName: spec.deviceName,
              scanNumber: removeLeadingZeros(spec.deviceSerialNumber),
            },
            viewDetailsAction,
            !isOnLiveScanRoute,
          ),
        );
        return;
      }

      if (spec.type === LIVE_SCAN_ALERT_TYPE.AWAITING_TRIM) {
        addToast(
          createDeviceToast(
            t,
            ToastLevel.WARN,
            "liveScan.toast.awaitingTrimSelection",
            params,
            viewDetailsAction,
            !isOnLiveScanRoute,
          ),
        );
        return;
      }

      if (spec.type === LIVE_SCAN_ALERT_TYPE.REPORT_GENERATED) {
        const action: DeviceToastAction = spec.hasViewableReport
          ? {
              labelKey: "liveScan.viewReport",
              onClick: () =>
                window.open(
                  `${PortalRoutes.BATTERY_REPORTS}/${spec.reportId}`,
                  "_blank",
                  "noopener,noreferrer",
                ),
            }
          : viewDetailsAction;

        const showActionButton = spec.hasViewableReport
          ? true
          : !isOnLiveScanRoute;

        addToast(
          createDeviceToast(
            t,
            ToastLevel.SUCCESS,
            "liveScan.toast.reportGenerated",
            params,
            action,
            showActionButton,
          ),
        );
      }
    },
    [addToast, t, isOnLiveScanRoute, setLocation, viewDetailsAction],
  );

  useLiveScanAlerts({ onAlert });
};
